// Strings of Settings › Chart template, the account nature and the onboarding link (migration 037,
// docs/chart-templates.md). Arabic marked "new" needs review by an Arabic copy editor.
export const TEMPLATE_STRINGS: Record<"en" | "ar", Record<string, string>> = {
  en: {
    setChartTemplate: "Chart template",
    tplIntro: "Install a ready chart of accounts into this organization. Delta shows the whole chart and its checks first; installing only adds groups and accounts. Nothing existing is replaced, merged, renumbered or deleted.",
    tplSupplied: "As supplied on {d}, version {v}. The title is the supplied document's own; Delta makes no claim that any authority approved it.",
    tplEntries: "Entries",
    tplGroups: "Groups (summary)",
    tplPosting: "Posting accounts",
    tplStatus: "Status",
    tplNotInstalled: "Not installed",
    tplInstalledOn: "Installed {d} by {u}: {g} groups and {a} accounts",
    tplExisting: "This organization's chart",
    tplExistingCounts: "{g} groups, {a} accounts, {l} with transactions ({n} journal lines)",
    tplChecks: "Validation",
    tplCorrections: "Corrections to the supplied chart",
    tplCorrectionsNote: "Only these two transcription errors are corrected; every other value is installed as supplied.",
    tplField: "Field",
    tplSuppliedValue: "Supplied",
    tplCorrected: "Installed as",
    tplReason: "Why",
    tplField_name: "Name",
    tplField_nature: "Nature",
    tplHierarchy: "Complete hierarchy",
    tplHierarchyNote: "Codes are kept as text, gaps included (there is no 5223). Classes come from the supplied account type, not from the code: codes starting with 4 are Revenues, with 5 Expenses.",
    tplType: "Account type (as supplied)",
    tplLevel: "Level",
    tplNature: "Nature",
    tplRole: "Function",
    tplState: "In this organization",
    tplNameEnNote: "English names are Delta's translations (the supplied chart is in Arabic); they can be edited after installing.",
    role_group: "Group (summary)",
    role_posting: "Posting (takes entries)",
    nature_debit: "Debit",
    nature_credit: "Credit",
    nature_both: "Debit or credit",
    natureNone: "Not stated",
    natureHint: "Information only: an entry on the other side still posts (contra and settlement accounts, reversals).",
    state_new: "Will be added",
    state_installed: "Installed",
    state_code_in_use: "Code already used",
    state_code_released: "Code used before",
    state_name_taken: "Name already used",
    tplConflictWith: "by {x}",
    tplInstall: "Install chart",
    tplInstallConfirm: "Install {g} groups and {a} posting accounts into {o}?",
    tplAlongside: "This organization already has {g} groups and {a} accounts. Add the template beside them (nothing existing changes).",
    tplInstalledOk: "Chart installed: {g} groups and {a} accounts",
    tplAlready: "This template is already installed; nothing was added.",
    tplOpenChart: "Open the chart tree",
    tplPrint: "Print hierarchy",
    tplBlocked: "Cannot install yet",
    blocker_already_installed: "Already installed in this organization.",
    blocker_template_invalid: "The template fails its checks.",
    blocker_conflicts: "Some template codes or names are already used in this organization's chart (see Conflicts below). Your existing chart needs your accountant's review before the template can be adopted; Delta changes nothing in it.",
    checkNotMet: "Not met: {rule}",
    checkConflictsFail: "{n} template entries meet a code or name already used in this organization's chart",
    checkConflictsSee: "See Conflicts below",
    tplConflicts: "Conflicts with this organization's chart",
    tplConflictsIntro: "These template entries use a code or a name that this organization's chart already uses. Your chart is in use, so nothing has been installed, and Delta will not delete, renumber, merge or replace any of your groups or accounts to make room.",
    tplConflictTemplate: "Template entry",
    tplConflictExisting: "Already in your chart",
    tplConflictMeaning: "What it means",
    tplConflictCode: "Code",
    tplConflictName: "Name",
    tplConflictRole: "Role",
    tplConflictPath: "Under",
    tplTopLevel: "Top level (a main class)",
    tplNoRecord: "No current record: the code was retired",
    kind_group: "group",
    kind_account: "posting account",
    conflict_code_in_use: "The same code is already used by your {k}. Two entries cannot share a code.",
    conflict_name_taken: "The same name is already used by your {k}, under another code. Delta keeps posting-account names unique, and group names unique among groups at the same level.",
    conflict_code_released: "This code was used in your chart before and was retired. Delta does not give a retired code out again, so the template entry cannot take it.",
    tplNextTitle: "What you can do next",
    tplNext1: "Print this comparison and give it to your accountant, who reviews your existing chart against the template.",
    tplNext2: "Keep working with your existing chart in the meantime: nothing on this page changes your accounts, balances or settings.",
    tplNext3: "Record how each existing account corresponds to the template in the adoption plan below, as the accountant decides. Saving the plan changes nothing.",
    tplNext4: "The template cannot be installed while any conflict remains. Delta does not resolve conflicts for you and does not install automatically.",
    tplPrintConflicts: "Print comparison",
    blocker_numbering: "The organization's numbering settings do not allow these codes.",
    blocker_not_owner: "Only the business owner installs a chart.",
    check_corrections_applied: "The two documented corrections apply",
    check_codes_unique: "No duplicate codes",
    check_codes_digits: "Codes are digits, kept as text",
    check_types_known: "Every account type maps to a Delta class",
    check_natures_known: "Every nature is debit, credit or either",
    check_roles_known: "Every entry is a group or a posting account",
    check_levels_read: "Every level is readable",
    check_names_present: "Every entry has an Arabic name",
    check_english_names_present: "Every entry has an English name",
    check_levels_match_tree: "Levels match the parent relationships",
    check_posting_accounts_have_no_children: "Posting accounts have nothing beneath them",
    check_posting_accounts_under_a_group: "Every posting account is under a group",
    check_class_same_as_parent: "Each entry is in its parent's class",
    check_names_unique_where_delta_requires: "Names are unique where Delta requires",
    check_no_conflicts_with_this_organization: "No code or name conflicts with this organization",
    check_numbering_max_length: "Codes fit the numbering length limit",
    check_numbering_parent_prefix: "Codes follow the parent-prefix setting",
    tplRefs: "Accounts used by settings",
    tplRefsNote: "Delta chooses none of these for you, and an account's name is not taken as approval. Review each setting and choose the account on its own screen. Accounts named after a bank, cash box or warehouse do not create one; VAT rates in names do not change tax settings; payroll accounts do not add HR.",
    tplSetting: "Setting",
    tplRecord: "Record",
    tplAccount: "Account",
    tplWhere: "Choose it in",
    tplNotSet: "Not chosen",
    tplFromTemplate: "template",
    ref_customerAccount: "Default customer account",
    ref_supplierAccount: "Default supplier account",
    ref_openingStockAccount: "Opening stock account",
    ref_stockDifferenceAccount: "Stock difference account",
    ref_itemKind: "Item kind",
    ref_taxType: "Tax type",
    ref_warehouse: "Warehouse",
    ref_treasury: "Treasury",
    ref_treasuryCommission: "Treasury commission",
    ref_assetKind: "Asset kind",
    reff_sales_account_id: "sales",
    reff_sales_refund_account_id: "sales refunds",
    reff_sales_cost_account_id: "cost of sales",
    reff_inventory_account_id: "inventory",
    reff_corrupted_account_id: "damaged stock",
    reff_discount_account_id: "discounts",
    reff_asset_account_id: "asset",
    reff_accumulated_account_id: "accumulated depreciation",
    reff_expense_account_id: "depreciation expense",
    tplOpen: "Open",
    tplPlan: "Adoption plan (for the accountant)",
    tplPlanNote: "This organization's chart is in use. Delta does not replace it, move balances or renumber accounts. Here you can record, account by account, which template account each existing account is meant to correspond to. Saving the plan changes nothing.",
    tplReplaceBlocked: "Replacing this chart is not offered: {a} accounts have transactions ({n} journal lines) and settings refer to accounts {r} times.",
    tplPlanFor: "Corresponds to",
    tplPlanNone: "Not decided",
    tplPlanLines: "Journal lines",
    tplPlanRefs: "Used by settings",
    tplPlanNote2: "Note",
    tplPlanSave: "Save plan",
    tplPlanSaved: "Plan saved; nothing in the chart changed",
    tplPlanBy: "Last saved {d} by {u}",
    tplPlanOwnerOnly: "Only the business owner saves the plan.",
    obInstallTemplate: "Install a chart template",
    obInstallTemplateHint: "Review the Saudi chart supplied for Delta and install it in one step, or set up your accounts yourself.",
    errTemplateConflicts: "Some codes or names of the template are already used here; nothing was installed.",
    errTemplateChanged: "The template changed since this page opened; it was reloaded.",
    errTemplateInvalid: "The template fails its checks; nothing was installed.",
    errTemplateVersion: "A different version of this template is installed.",
    errAlongside: "Confirm adding the template beside the existing chart.",
    errNumbering: "The numbering settings do not allow the template's codes."
  },
  ar: {
    setChartTemplate: "قالب دليل الحسابات", // new
    tplIntro: "ثبّت دليل حسابات جاهزًا في هذه المنشأة. يعرض دلتا الدليل كاملًا ونتائج فحصه أولًا، ولا يضيف التثبيت إلا مجموعات وحسابات. لا يُستبدل شيء قائم ولا يُدمج ولا يُعاد ترقيمه ولا يُحذف.", // new
    tplSupplied: "كما ورد بتاريخ {d}، الإصدار {v}. العنوان هو عنوان المستند المقدَّم، ولا يدّعي دلتا اعتماده من أي جهة.", // new
    tplEntries: "البنود", // new
    tplGroups: "حسابات تجميعية", // new
    tplPosting: "حسابات ترحيل", // new
    tplStatus: "الحالة", // new
    tplNotInstalled: "غير مثبت", // new
    tplInstalledOn: "ثُبّت في {d} بواسطة {u}: {g} مجموعة و{a} حسابًا", // new
    tplExisting: "دليل حسابات هذه المنشأة", // new
    tplExistingCounts: "{g} مجموعة، {a} حسابًا، منها {l} عليها حركات ({n} سطر قيد)", // new
    tplChecks: "الفحص", // new
    tplCorrections: "تصحيحات على الدليل المقدَّم", // new
    tplCorrectionsNote: "لا يُصحَّح إلا هذان الخطآن في النسخ، وتُثبَّت سائر القيم كما وردت.", // new
    tplField: "الحقل", // new
    tplSuppliedValue: "كما ورد", // new
    tplCorrected: "يُثبَّت بقيمة", // new
    tplReason: "السبب", // new
    tplField_name: "الاسم", // new
    tplField_nature: "الطبيعة", // new
    tplHierarchy: "الشجرة كاملة", // new
    tplHierarchyNote: "تُحفظ الأرقام نصًا مع فجواتها (لا يوجد 5223). يُحدَّد التصنيف من نوع الحساب الوارد لا من الرقم: الأرقام التي تبدأ بـ4 إيرادات، وبـ5 مصروفات.", // new
    tplType: "نوع الحساب (كما ورد)", // new
    tplLevel: "المستوى", // new
    tplNature: "طبيعة الحساب", // new
    tplRole: "وظيفة الحساب", // new
    tplState: "في هذه المنشأة", // new
    tplNameEnNote: "الأسماء الإنجليزية ترجمة من دلتا (الدليل المقدَّم بالعربية)، ويمكن تعديلها بعد التثبيت.", // new
    role_group: "حساب تجميعي (رئيسي)", // new
    role_posting: "حساب ترحيل (يقبل القيود)", // new
    nature_debit: "مدين", // new
    nature_credit: "دائن", // new
    nature_both: "دائن / مدين", // new
    natureNone: "غير محددة", // new
    natureHint: "للعلم فقط: يُقبل القيد في الجانب الآخر (الحسابات العكسية وحسابات التسوية والعكس).", // new
    state_new: "سيُضاف", // new
    state_installed: "مثبت", // new
    state_code_in_use: "الرقم مستخدم", // new
    state_code_released: "الرقم استُخدم سابقًا", // new
    state_name_taken: "الاسم مستخدم", // new
    tplConflictWith: "في {x}", // new
    tplInstall: "تثبيت الدليل", // new
    tplInstallConfirm: "تثبيت {g} مجموعة و{a} حساب ترحيل في {o}؟", // new
    tplAlongside: "لدى هذه المنشأة {g} مجموعة و{a} حسابًا. أضف القالب بجانبها (لا يتغير شيء قائم).", // new
    tplInstalledOk: "ثُبّت الدليل: {g} مجموعة و{a} حسابًا", // new
    tplAlready: "هذا القالب مثبت مسبقًا، ولم يُضف شيء.", // new
    tplOpenChart: "فتح شجرة الحسابات", // new
    tplPrint: "طباعة الشجرة", // new
    tplBlocked: "لا يمكن التثبيت الآن", // new
    blocker_already_installed: "مثبت في هذه المنشأة.", // new
    blocker_template_invalid: "القالب لا يجتاز الفحص.", // new
    blocker_conflicts: "بعض أرقام القالب أو أسمائه مستخدمة في دليل هذه المنشأة (انظر «التعارضات» أدناه). يحتاج دليلك القائم إلى مراجعة المحاسب قبل اعتماد القالب، ولا يغيّر دلتا فيه شيئًا.", // new
    checkNotMet: "غير متحقق: {rule}", // new
    checkConflictsFail: "{n} من بنود القالب تستخدم رقمًا أو اسمًا مستخدمًا في دليل هذه المنشأة", // new
    checkConflictsSee: "انظر «التعارضات» أدناه", // new
    tplConflicts: "التعارضات مع دليل هذه المنشأة", // new
    tplConflictsIntro: "تستخدم بنود القالب التالية رقمًا أو اسمًا مستخدمًا في دليل هذه المنشأة. دليلك مستخدم، لذلك لم يُثبَّت شيء، ولن يحذف دلتا أي حساب أو مجموعة لديك أو يعيد ترقيمها أو يدمجها أو يستبدلها لإفساح المجال.", // new
    tplConflictTemplate: "بند القالب", // new
    tplConflictExisting: "الموجود في دليلك", // new
    tplConflictMeaning: "ماذا يعني ذلك", // new
    tplConflictCode: "الرقم", // new
    tplConflictName: "الاسم", // new
    tplConflictRole: "النوع", // new
    tplConflictPath: "يقع تحت", // new
    tplTopLevel: "المستوى الأعلى (تصنيف رئيسي)", // new
    tplNoRecord: "لا يوجد سجل حالي: الرقم أُوقف", // new
    kind_group: "حساب تجميعي", // new
    kind_account: "حساب ترحيل", // new
    conflict_code_in_use: "الرقم نفسه مستخدم في {k} لديك، ولا يجوز أن يشترك بندان في رقم واحد.", // new
    conflict_name_taken: "الاسم نفسه مستخدم في {k} لديك برقم آخر. يُبقي دلتا أسماء حسابات الترحيل غير مكررة، وأسماء الحسابات التجميعية غير مكررة في المستوى نفسه.", // new
    conflict_code_released: "استُخدم هذا الرقم في دليلك من قبل ثم أُوقف. لا يعيد دلتا استخدام رقم موقوف، فلا يمكن أن يأخذه بند القالب.", // new
    tplNextTitle: "ما يمكنك فعله الآن", // new
    tplNext1: "اطبع هذه المقارنة وسلّمها لمحاسبك ليراجع دليلك القائم مقابل القالب.", // new
    tplNext2: "واصل العمل بدليلك القائم في الأثناء: لا شيء في هذه الصفحة يغيّر حساباتك أو أرصدتك أو إعداداتك.", // new
    tplNext3: "سجّل في خطة الاعتماد أدناه ما يقابل كل حساب قائم في القالب، وفق قرار المحاسب. حفظ الخطة لا يغيّر شيئًا.", // new
    tplNext4: "لا يمكن تثبيت القالب ما دام أي تعارض قائمًا. لا يحل دلتا التعارضات نيابة عنك ولا يثبّت تلقائيًا.", // new
    tplPrintConflicts: "طباعة المقارنة", // new
    blocker_numbering: "إعدادات الترقيم في المنشأة لا تسمح بهذه الأرقام.", // new
    blocker_not_owner: "صاحب المنشأة وحده يثبّت الدليل.", // new
    check_corrections_applied: "التصحيحان الموثقان مطبقان", // new
    check_codes_unique: "لا أرقام مكررة", // new
    check_codes_digits: "الأرقام أرقام فقط وتُحفظ نصًا", // new
    check_types_known: "كل نوع حساب يقابل تصنيفًا في دلتا", // new
    check_natures_known: "كل طبيعة مدين أو دائن أو كلاهما", // new
    check_roles_known: "كل بند تجميعي أو ترحيل", // new
    check_levels_read: "كل مستوى مقروء", // new
    check_names_present: "لكل بند اسم عربي", // new
    check_english_names_present: "لكل بند اسم إنجليزي", // new
    check_levels_match_tree: "المستويات تطابق علاقات الأب", // new
    check_posting_accounts_have_no_children: "لا شيء تحت حسابات الترحيل", // new
    check_posting_accounts_under_a_group: "كل حساب ترحيل تحت مجموعة", // new
    check_class_same_as_parent: "كل بند في تصنيف أبيه", // new
    check_names_unique_where_delta_requires: "الأسماء غير مكررة حيث يشترط دلتا", // new
    check_no_conflicts_with_this_organization: "لا تعارض في الأرقام أو الأسماء مع هذه المنشأة", // new
    check_numbering_max_length: "الأرقام ضمن الطول المسموح في الترقيم", // new
    check_numbering_parent_prefix: "الأرقام تتبع إعداد بادئة الأب", // new
    tplRefs: "الحسابات المستخدمة في الإعدادات", // new
    tplRefsNote: "لا يختار دلتا أيًا منها نيابة عنك، ولا يُعد تشابه الاسم موافقة. راجع كل إعداد واختر الحساب من شاشته. الحسابات المسماة باسم بنك أو صندوق أو مستودع لا تُنشئه، ونسب الضريبة في الأسماء لا تغيّر إعدادات الضريبة، وحسابات الرواتب لا تضيف الموارد البشرية.", // new
    tplSetting: "الإعداد", // new
    tplRecord: "السجل", // new
    tplAccount: "الحساب", // new
    tplWhere: "يُختار في", // new
    tplNotSet: "لم يُختر", // new
    tplFromTemplate: "من القالب", // new
    ref_customerAccount: "حساب العملاء الافتراضي", // new
    ref_supplierAccount: "حساب الموردين الافتراضي", // new
    ref_openingStockAccount: "حساب مخزون أول المدة", // new
    ref_stockDifferenceAccount: "حساب فروقات المخزون", // new
    ref_itemKind: "نوع الصنف", // new
    ref_taxType: "نوع الضريبة", // new
    ref_warehouse: "المستودع", // new
    ref_treasury: "الخزينة", // new
    ref_treasuryCommission: "عمولة الخزينة", // new
    ref_assetKind: "نوع الأصل", // new
    reff_sales_account_id: "المبيعات", // new
    reff_sales_refund_account_id: "مرتجع المبيعات", // new
    reff_sales_cost_account_id: "تكلفة المبيعات", // new
    reff_inventory_account_id: "المخزون", // new
    reff_corrupted_account_id: "التالف", // new
    reff_discount_account_id: "الخصومات", // new
    reff_asset_account_id: "الأصل", // new
    reff_accumulated_account_id: "مجمع الإهلاك", // new
    reff_expense_account_id: "مصروف الإهلاك", // new
    tplOpen: "فتح", // new
    tplPlan: "خطة الاعتماد (للمحاسب)", // new
    tplPlanNote: "دليل حسابات هذه المنشأة مستخدم. لا يستبدله دلتا ولا ينقل الأرصدة ولا يعيد الترقيم. يمكنك هنا تسجيل الحساب المقابل في القالب لكل حساب قائم، حسابًا حسابًا. حفظ الخطة لا يغيّر شيئًا.", // new
    tplReplaceBlocked: "استبدال هذا الدليل غير متاح: {a} حسابًا عليها حركات ({n} سطر قيد)، وتشير الإعدادات إلى الحسابات {r} مرة.", // new
    tplPlanFor: "يقابل", // new
    tplPlanNone: "لم يُحدد", // new
    tplPlanLines: "أسطر القيود", // new
    tplPlanRefs: "مستخدم في الإعدادات", // new
    tplPlanNote2: "ملاحظة", // new
    tplPlanSave: "حفظ الخطة", // new
    tplPlanSaved: "حُفظت الخطة، ولم يتغير شيء في الدليل", // new
    tplPlanBy: "آخر حفظ {d} بواسطة {u}", // new
    tplPlanOwnerOnly: "صاحب المنشأة وحده يحفظ الخطة.", // new
    obInstallTemplate: "تثبيت قالب دليل الحسابات", // new
    obInstallTemplateHint: "راجع الدليل السعودي المقدَّم لدلتا وثبّته بخطوة واحدة، أو أعدّ حساباتك بنفسك.", // new
    errTemplateConflicts: "بعض أرقام القالب أو أسمائه مستخدمة هنا، ولم يُثبَّت شيء.", // new
    errTemplateChanged: "تغيّر القالب منذ فتح الصفحة، وأُعيد تحميله.", // new
    errTemplateInvalid: "القالب لا يجتاز الفحص، ولم يُثبَّت شيء.", // new
    errTemplateVersion: "إصدار آخر من هذا القالب مثبت.", // new
    errAlongside: "أكّد إضافة القالب بجانب الدليل القائم.", // new
    errNumbering: "إعدادات الترقيم لا تسمح بأرقام القالب." // new
  }
};
